import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';
import { applyWarehouseFilter, warehouseParamsSerializer, appendWarehouseParams } from '../utils/warehouseParams';
import purchaseOrderService from './purchaseOrderService';
import salesService from './salesService';
import deliveryChallanService from './deliveryChallanService';
import { registerClearable } from '../utils/cacheRegistry';

// Axios instance for orders API
const ordersAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: warehouseParamsSerializer,
});

// Attach auth token if available
ordersAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;
    return applyWarehouseFilter(config);
  },
  (error) => Promise.reject(error)
);

// Redirect on auth failure
ordersAPI.interceptors.response.use(
  (res) => res,
  (err) => {
    // Skip logout on 401 for bulk-delete endpoints (which use 401 for password errors)
    if (err.response?.status === 401 && !err.config?.url?.includes('/bulk-delete/')) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("access_token");
      localStorage.removeItem("user_data");
      sessionStorage.removeItem("auth_token");
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("user_data");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  },
);

const BASE_URL = '/orders';

// Caching helpers (memory + sessionStorage)
const CACHE_PREFIX = 'erp_orders_cache_v1';
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

const memoryCache = new Map();

function cacheKey(key) {
  return `${CACHE_PREFIX}::${key}`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires) return null;
    if (Date.now() > parsed.expires) {
      sessionStorage.removeItem(cacheKey(key));
      return null;
    }
    return parsed.value;
  } catch (e) {
    return null;
  }
}

function writeCache(key, value, ttl = DEFAULT_TTL_MS) {
  try {
    sessionStorage.setItem(cacheKey(key), JSON.stringify({ value, expires: Date.now() + ttl }));
  } catch (e) { }
}

function readMemoryCache(key) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expires) {
    memoryCache.delete(key);
    return null;
  }
  return entry.value;
}

function writeMemoryCache(key, value, ttl = DEFAULT_TTL_MS) {
  memoryCache.set(key, { value, expires: Date.now() + ttl });
}

function deleteCacheEntry(key) {
  try { memoryCache.delete(key); } catch (e) { }
  try { sessionStorage.removeItem(cacheKey(key)); } catch (e) { }
}

// A cached full list is either a bare array or a paginated `{ items, total }`
// envelope (the newer list endpoints). The modifier used to run only on bare
// arrays, so an envelope kept serving the stale row after an update/delete (P1-4).
function applyListModifier(value, modifierFn) {
  if (Array.isArray(value)) return modifierFn(value.slice());
  if (value && Array.isArray(value.items)) {
    const items = modifierFn(value.items.slice());
    const delta = items.length - value.items.length;
    const total = typeof value.total === 'number' ? Math.max(0, value.total + delta) : value.total;
    return { ...value, items, total };
  }
  return null;
}

function updateFullListCachesWithModifier(modifierFn) {
  try {
    for (const [key, entry] of memoryCache.entries()) {
      if (typeof key === 'string' && key.startsWith('all::')) {
        const next = applyListModifier(entry?.value, modifierFn);
        if (next !== null) writeMemoryCache(key, next);
      }
    }
  } catch (e) { }

  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const sk = sessionStorage.key(i);
      if (!sk) continue;
      if (sk.startsWith(`${CACHE_PREFIX}::all::`)) {
        const raw = sessionStorage.getItem(sk);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          const next = applyListModifier(parsed?.value, modifierFn);
          if (next !== null) {
            parsed.value = next;
            sessionStorage.setItem(sk, JSON.stringify(parsed));
          }
        } catch (e) { }
      }
    }
  } catch (e) { }
}


/**
 * Deleting a sales order invalidates more than this service's own cache: the Proforma
 * Invoice screen reads the sales daybook (salesService) and the challan screens read
 * deliveryChallanService, both of which cache to sessionStorage. Clearing only
 * ordersService left those serving deleted rows for the rest of their TTL, so the
 * records reappeared on the next load.
 *
 * Kept as one helper so every delete path invalidates the same set.
 */
function clearSalesOrderDerivedCaches(self) {
    self.clearCache();
    try { salesService.clearCache(); } catch (e) { /* non-fatal */ }
    try { deliveryChallanService.clearCache(); } catch (e) { /* non-fatal */ }
}

const ordersService = {
  async getAllOrders(params = {}, options = {}) {
    const { force = false } = options;
    const key = `all::${JSON.stringify(params || {})}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) { writeMemoryCache(key, sess); return { success: true, data: sess }; }
    }

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params.skip !== undefined) queryParams.append('skip', String(params.skip));
      if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
      if (params.search !== undefined && params.search !== '') {
        queryParams.append('search', params.search);
      }
      if (params.from_date) queryParams.append('from_date', params.from_date);
      if (params.to_date) queryParams.append('to_date', params.to_date);
      if (params.status) {
        const statuses = Array.isArray(params.status) ? params.status : [params.status];
        statuses.forEach(s => queryParams.append('status', s));
      }
      if (params.gst_included !== undefined) {
        queryParams.append('gst_included', String(params.gst_included));
      }
      appendWarehouseParams(queryParams);

      const response = await ordersAPI.get(`${BASE_URL}?${queryParams.toString()}`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async getOrderById(orderId, options = {}) {
    const { force = false } = options;
    const key = `byId::${orderId}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) { writeMemoryCache(key, sess); return { success: true, data: sess }; }

      // fallback: search in a cached full-list
      const fullKey = `all::${JSON.stringify({})}`;
      const fullMem = readMemoryCache(fullKey) || readCache(fullKey);
      if (fullMem && Array.isArray(fullMem)) {
        const found = fullMem.find((i) => String(i.id) === String(orderId));
        if (found) { writeMemoryCache(key, found); writeCache(key, found); return { success: true, data: found }; }
      }
    }

    try {
      const response = await ordersAPI.get(`${BASE_URL}/${orderId}`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createOrder(orderPayload) {
    try {
      let response;

      if (orderPayload && typeof orderPayload === 'object' && orderPayload.customer) {
        response = await ordersAPI.post(`${BASE_URL}/new-customer`, orderPayload);
      } else if (orderPayload && (orderPayload.customer_id || orderPayload.customerId)) {
        response = await ordersAPI.post(`${BASE_URL}/existing-customer`, orderPayload);
      } else {
        response = await ordersAPI.post(BASE_URL, orderPayload);
      }

      const created = response.data;

      // prepend to cached full-lists
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });

      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }

      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createOrderForNewCustomer(payload) {
    try {
      const response = await ordersAPI.post(`${BASE_URL}/new-customer`, payload);
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createOrderForExistingCustomer(payload) {
    try {
      const response = await ordersAPI.post(`${BASE_URL}/existing-customer`, payload);
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createOrderForExistingCustomerWithAttachments(payload, files = []) {
    try {
      const fd = new FormData();
      fd.append('payload', JSON.stringify(payload));
      files.forEach(f => fd.append('files', f));
      const response = await ordersAPI.post(`${BASE_URL}/existing-customer/with-attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createOrderForNewCustomerWithAttachments(payload, files = []) {
    try {
      const fd = new FormData();
      fd.append('payload', JSON.stringify(payload));
      files.forEach(f => fd.append('files', f));
      const response = await ordersAPI.post(`${BASE_URL}/new-customer/with-attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async updateOrder(orderId, orderPayload) {
    try {
      const response = await ordersAPI.put(`${BASE_URL}/${orderId}`, orderPayload);
      const updated = response.data;

      updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(orderId) ? updated : i)));

      try { const key = `byId::${orderId}`; writeMemoryCache(key, updated); writeCache(key, updated); } catch (e) { }

      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async confirmOrder(orderId) {
    try {
      const response = await ordersAPI.post(`${BASE_URL}/${orderId}/confirm`);
      const updated = response.data;

      updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(orderId) ? updated : i)));
      try { const key = `byId::${orderId}`; writeMemoryCache(key, updated); writeCache(key, updated); } catch (e) { }

      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // `revertStock` decides what happens to the inventory this order already moved: true
  // gives it back (the challan-time debit, the pending reservation booked at create
  // time, any coils), false removes only the paperwork and leaves every balance
  // standing. The API defaults to reverting, and so does every caller that does not ask
  // the operator.
  async deleteOrder(orderId, { revertStock = true } = {}) {
    try {
      const response = await ordersAPI.delete(`${BASE_URL}/${orderId}`, {
        params: { revert_stock: revertStock },
      });
      updateFullListCachesWithModifier((arr) => arr.filter((i) => String(i.id) !== String(orderId)));
      try { deleteCacheEntry(`byId::${orderId}`); } catch (e) { }
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async cancelOrder(orderId) {
    try {
      const response = await ordersAPI.post(`${BASE_URL}/${orderId}/cancel`);
      const updated = response.data;
      updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(orderId) ? updated : i)));
      try { const key = `byId::${orderId}`; writeMemoryCache(key, updated); writeCache(key, updated); } catch (e) { }
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // Delivery Challans CRUD
  async getAllDeliveryChallans(params = {}, options = {}) {
    try {
      const response = await ordersAPI.get('/delivery-challans', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  /**
   * Delete ALL orders across Sales and Purchase modules.
   * Requires super admin password.
   * `revertStock: false` keeps every warehouse balance where it stands and removes
   * only the paperwork; the default gives the stock back.
   */
  async bulkDeleteAllOrdersGlobal(payload, { revertStock = false } = {}) {
      try {
          const response = await ordersAPI.post('/bulk-delete/all-orders', { ...payload, revert_stock: revertStock });
          if (response.data?.success || response.status === 200) {
              clearSalesOrderDerivedCaches(this);
              purchaseOrderService.clearCache();
          }
          return { success: true, ...response.data };
      } catch (error) {
          return { success: false, error: extractApiError(error) };
      }
  },

  /**
   * Delete ALL Sales Orders (delivery orders, sub-orders, items, challans).
   * Requires super admin password.
   */
  async bulkDeleteSalesOrders(payload, { revertStock = false } = {}) {
      try {
          const response = await ordersAPI.post('/bulk-delete/sales-orders', { ...payload, revert_stock: revertStock });
          if (response.data?.success || response.status === 200) {
              clearSalesOrderDerivedCaches(this);
          }
          return { success: true, ...response.data };
      } catch (error) {
          return { success: false, error: extractApiError(error) };
      }
  },

  /**
   * Delete ALL Purchase Orders (advices, advice items, challans).
   * Requires super admin password.
   */
  async bulkDeletePurchaseOrdersGlobal(payload, { revertStock = false } = {}) {
      try {
          const response = await ordersAPI.post('/bulk-delete/purchase-orders', { ...payload, revert_stock: revertStock });
          if (response.data?.success || response.status === 200) {
              purchaseOrderService.clearCache();
          }
          return { success: true, ...response.data };
      } catch (error) {
          return { success: false, error: extractApiError(error) };
      }
  },

  /**
   * Delete specific Sales and/or Purchase orders by ID.
   * Payload: { password, order_ids: [], purchase_order_ids: [] }
   * Requires super admin password.
   */
  // Payload: { password, order_ids?, sub_order_ids?, purchase_order_ids?, advice_ids? }.
  // Sales Daybook / Proforma send `sub_order_ids` and Purchase Daybook / Invoice-Purchase
  // send `advice_ids` (their rows are challans/advices): the backend resolves each to its
  // parent order/PO, so a challan or advice id can never be mistaken for an unrelated id.
  async bulkDeleteSelectedOrders(payload, { revertStock = false } = {}) {
      try {
          const response = await ordersAPI.post('/bulk-delete/selected-orders', { ...payload, revert_stock: revertStock });
          if (response.data?.success || response.status === 200) {
              if (payload.order_ids?.length || payload.sub_order_ids?.length) clearSalesOrderDerivedCaches(this);
              if (payload.purchase_order_ids?.length || payload.advice_ids?.length) purchaseOrderService.clearCache();
          }
          return { success: true, ...response.data };
      } catch (error) {
          return { success: false, error: extractApiError(error) };
      }
  },

  async sendOrderEmail(orderId) {
    try {
      const response = await ordersAPI.post(`${BASE_URL}/${orderId}/send-email`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async sendOrderNotification(orderId, type = 'email') {
    try {
      const response = await ordersAPI.post(`${BASE_URL}/${orderId}/send-notification`, null, { params: { type } });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async getVehicleTracking(params = {}) {
    try {
      const response = await ordersAPI.get('/vehicle-tracking', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async exportDeliveryChallans(params = {}) {
    try {
      const response = await ordersAPI.get('/delivery-challans/export/download', {
        params,
        responseType: 'blob',
      });
      return { success: true, data: response.data, headers: response.headers };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  clearCache() {
    try {
      // Memory keys are "all::…" / "byId::…" — the old substring match never hit, so
      // the in-memory list outlived clearCache() and kept serving stale rows (P1-4).
      memoryCache.clear();
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k);
      }
    } catch (e) { }
  }
};

// Cleared on logout together with the other services (policy C16).
registerClearable(() => ordersService.clearCache());

export default ordersService;
